import { type FragmentType, useFragment } from '@/gql/codegen';
import { graphql } from '@/gql/codegen/gql';
import { Link } from '@tanstack/react-router';
import classNames from 'classnames';
import type React from 'react';
import styles from './index.module.scss';

import { UserEnrollmentStatus } from '@/components/UserEnrollmentStatus';
import { ImportantNotificationBlock } from '@/routes/_embaUserLayout/-components/ImportantNotificationBlock';
import { LearnerPageTitle } from '@/routes/_embaUserLayout/-components/LearnerPageTitle';
import { ProgressBar } from '@/routes/_embaUserLayout/-components/ProgressBar';

export const USER_ENROLLMENT = graphql(/* GraphQL */ `
  fragment UserEnrollment on UserEnrollment {
    id
    startDateText
    endDateText
    scheduleStatus
    scheduleStatusText
    isTrial
    applicationType
    enrollmentCourses{
      id
      status
      statusText
      displayPeriod
      embaCourse {
        id
        name
        slug
        thumbnailImageUrl
      }
      latestAttendance {
        id
        slug
        attendedCountText
        progress
        status
      }
    }
  }
`);

export const IMPORTANT_NOTIFICATION = graphql(/* GraphQL */ `
  fragment EmbaImportantNotification on ImportantNotification {
    id
    description
    url
    publishAtFromText
  }
`);

export type UserEnrollmentPageProps = {
  currentEmbaUserEnrollment: FragmentType<typeof USER_ENROLLMENT>;
  importantNotification: FragmentType<typeof IMPORTANT_NOTIFICATION> | null;
};

export const UserEnrollmentPage = ({
  currentEmbaUserEnrollment,
  importantNotification,
}: UserEnrollmentPageProps) => {
  const embaUserEnrollmentCoursesData = useFragment(
    USER_ENROLLMENT,
    currentEmbaUserEnrollment,
  );
  const importantNotificationData = useFragment(
    IMPORTANT_NOTIFICATION,
    importantNotification,
  );
  const showEmbaOnlyTrialNotice =
    embaUserEnrollmentCoursesData.applicationType === 'emba_only_trial';
  const showHodaiPlusTrialNotice =
    embaUserEnrollmentCoursesData.applicationType === 'hodai_plus_trial';

  return (
    <div className={styles.embaUserEnrollmentCourses}>
      {importantNotificationData && (
        <ImportantNotificationBlock
          dateStr={importantNotificationData.publishAtFromText}
          description={importantNotificationData.description}
          url={importantNotificationData.url}
        />
      )}
      <ul className={styles.breadCrumb}>
        <li>科目一覧</li>
      </ul>
      <UserEnrollmentStatus
        scheduleStatus={embaUserEnrollmentCoursesData.scheduleStatus}
        scheduleStatusText={embaUserEnrollmentCoursesData.scheduleStatusText}
        enrollmentCoursesLength={
          embaUserEnrollmentCoursesData.enrollmentCourses.length
        }
        startDateText={embaUserEnrollmentCoursesData.startDateText}
        endDateText={embaUserEnrollmentCoursesData.endDateText}
      />
      <LearnerPageTitle title="科目一覧" />
      {showEmbaOnlyTrialNotice && (
        <div className={styles.embaOnlyTrialNotice}>
          <p className={styles.embaOnlyTrialNoticeTitle}>
            【eMBAトライアルのご利用について】
          </p>
          <div className={styles.embaOnlyTrialNoticeContent}>
            <div className={styles.embaOnlyTrialNoticeSection}>
              ・体験できる科目
              <br />
              {'　eMBAでご提供するすべての科目をご体験いただけます。'}
              <br />
              {'　（AI対話トレーニング科目、グロービスMBA科目、ショート科目）'}
            </div>
            <div className={styles.embaOnlyTrialNoticeSection}>
              ・学習できる範囲
              <br />
              {
                '　Webサイト経由で発行されたトライアルIDの場合、各科目の学習内容は一部に制限されています。'
              }
              <br />
              {'　全ての学習の体験をご希望の場合は'}
              <a
                href="https://pages.globis.co.jp/gdp_inquiry_emba.html"
                target="_blank"
                rel="noreferrer"
                className={styles.embaOnlyTrialNoticeLink}
              >
                こちら
              </a>
              よりお問い合わせください。
            </div>
          </div>
          <div className={styles.embaOnlyTrialNoticeAnnotation}>
            ※「AI対話トレーニング科目」は、GLOBIS 学び放題とeMBAのセットプラン「
            <a
              href="https://gce.globis.co.jp/service/hodai/plan/plus/"
              target="_blank"
              rel="noreferrer"
              className={styles.embaOnlyTrialNoticeLink}
            >
              GLOBIS 学び放題 プラス
            </a>
            」には含まれておりません。
          </div>
        </div>
      )}
      {showHodaiPlusTrialNotice && (
        <div className={styles.hodaiPlusTrialNotice}>
          <div className={styles.hodaiPlusTrialNoticeContent}>
            本トライアルIDでは、「GLOBIS 学び放題 プラス」として学習いただける、
            <br />
            eMBAの「グロービスMBA科目」「ショート科目」の合計14科目をご体験いただけます。
          </div>
          <div className={styles.hodaiPlusTrialNoticeAnnotation}>
            ※AIとの音声対話で対人スキルを鍛える「
            <a
              href="https://gce.globis.co.jp/service/training-type/e-learning/emba/curriculum/ai/"
              target="_blank"
              rel="noreferrer"
              className={styles.hodaiPlusTrialNoticeLink}
            >
              AI対話トレーニング科目
            </a>
            」は、「GLOBIS 学び放題 プラス」には含まれておりません。
            <br />
            {'　トライアルをご希望の場合は担当営業までお問い合わせください。'}
          </div>
        </div>
      )}
      <ul className={styles.courseList}>
        {embaUserEnrollmentCoursesData.enrollmentCourses.map(
          (enrollmentCourse) => (
            <li key={enrollmentCourse.id} className={styles.enrollmentCourse}>
              <div className={styles.courseInfo}>
                <h3>
                  {enrollmentCourse.status === 'completed' && (
                    <span className={styles.completedStatusIcon}>
                      {enrollmentCourse.statusText}
                    </span>
                  )}

                  <p className={styles.courseTitle}>
                    {enrollmentCourse.embaCourse.name}
                  </p>
                </h3>
                <div className={styles.attendanceArea}>
                  <span className={styles.courseImageArea}>
                    <img
                      src={enrollmentCourse.embaCourse.thumbnailImageUrl}
                      alt=""
                    />
                  </span>
                  <div className={styles.progress}>
                    <p className={styles.attendedCount}>
                      {enrollmentCourse.latestAttendance.attendedCountText}
                    </p>
                    <ProgressBar
                      progress={enrollmentCourse.latestAttendance.progress}
                    />
                  </div>
                </div>
              </div>
              <div
                className={classNames(
                  styles.linkArea,
                  styles[enrollmentCourse.latestAttendance.status],
                )}
              >
                {enrollmentCourse.latestAttendance.status !== 'completed' && (
                  <ul className={styles.linkList}>
                    <li>
                      <Link
                        to="/enrollment-course/$enrollmentCourseId"
                        params={{ enrollmentCourseId: enrollmentCourse.id }}
                        className={styles.courseLink}
                      >
                        受講状況を見る
                      </Link>
                    </li>
                  </ul>
                )}

                <div className={styles.lessonLinkArea}>
                  {enrollmentCourse.latestAttendance.status === 'completed' ? (
                    <div>
                      <Link
                        to="/enrollment-course/$enrollmentCourseId"
                        params={{ enrollmentCourseId: enrollmentCourse.id }}
                        className={styles.lessonLink}
                      >
                        受講状況を見る
                      </Link>
                    </div>
                  ) : (
                    <div>
                      <Link
                        to="/enrollment-course/$enrollmentCourseId/attendance/$attendanceSlug/redirect-recommended-content"
                        params={{
                          enrollmentCourseId: enrollmentCourse.id,
                          attendanceSlug:
                            enrollmentCourse.latestAttendance.slug,
                        }}
                        className={styles.lessonLink}
                      >
                        受講する
                      </Link>
                    </div>
                  )}
                  <div>
                    <Link
                      to="/enrollment-course/$enrollmentCourseId/attendance/$attendanceSlug"
                      params={{
                        enrollmentCourseId: enrollmentCourse.id,
                        attendanceSlug: enrollmentCourse.latestAttendance.slug,
                      }}
                      className={styles.lessonAllLink}
                    >
                      レッスン一覧を見る
                    </Link>
                  </div>
                </div>
              </div>
            </li>
          ),
        )}
      </ul>
    </div>
  );
};
